// src/Utils/generateTermsPDF.js
import { jsPDF } from "jspdf";

const PCM_INFO = {
  name: "PCM EXPORTS",
  address:
    "Shop No. 104, Block-A, Bharati Tower, Forestpark Ekamra Marg, Bhubaneswar, Khordha, Odisha(India) - 751009",
  tel: "+91-08062177433",
  email: "pcmexportsbbsr@gmail.com",
  web: "www.pcmexports.com",
  gstin: "21BBFPM2533K1ZW",
  pan: "BILPS0248G",
};

// Brand colors
const PRIMARY = [220, 38, 38]; // red-600  (adjust to your primaryColor)
const DARK = [31, 41, 55]; // gray-800
const MID = [107, 114, 128]; // gray-500
const LIGHT = [243, 244, 246]; // gray-100
const WHITE = [255, 255, 255];

const PAGE_W = 210; // A4 mm
const PAGE_H = 297;
const MARGIN = 18;
const CONTENT_W = PAGE_W - MARGIN * 2;

// ── helpers ──────────────────────────────────────────────────────────────────

/** wrap text and return array of lines */
const wrap = (doc, text, maxWidth) => doc.splitTextToSize(text, maxWidth);

/** draw a filled rounded rect */
const fillRect = (doc, x, y, w, h, r, color) => {
  doc.setFillColor(...color);
  doc.roundedRect(x, y, w, h, r, r, "F");
};

/** draw horizontal rule */
const hr = (doc, y, color = [229, 231, 235]) => {
  doc.setDrawColor(...color);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
};

// ── page header (logo area + company info) ───────────────────────────────────
const drawHeader = (doc, logoDataUrl) => {
  // Top bar
  fillRect(doc, 0, 0, PAGE_W, 38, 0, PRIMARY);

  // Logo — placed left inside the bar
  if (logoDataUrl) {
    try {
      // Draw white pill behind logo for contrast
      fillRect(doc, MARGIN - 2, 4, 38, 30, 4, WHITE);
      doc.addImage(logoDataUrl, "PNG", MARGIN, 6, 34, 26, undefined, "FAST");
    } catch (_) {
      // logo load failed — skip silently
    }
  }

  // Company name + tagline (right of logo)
  const textX = logoDataUrl ? MARGIN + 42 : MARGIN;
  doc.setTextColor(...WHITE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(PCM_INFO.name, textX, 15);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("Quick Quotation & Trade (QQT)", textX, 21);

  // Contact strip (right-aligned inside bar)
  const contactX = PAGE_W - MARGIN;
  doc.setFontSize(7);
  doc.text(PCM_INFO.tel, contactX, 12, { align: "right" });
  doc.text(PCM_INFO.email, contactX, 17, { align: "right" });
  doc.text(PCM_INFO.web, contactX, 22, { align: "right" });

  // Address band below bar
  fillRect(doc, 0, 38, PAGE_W, 10, 0, [254, 242, 242]); // light red tint
  doc.setTextColor(...MID);
  doc.setFontSize(6.8);
  doc.text(PCM_INFO.address, PAGE_W / 2, 44, { align: "center" });

  // GSTIN / PAN
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(...PRIMARY);
  doc.text(`GSTIN: ${PCM_INFO.gstin}`, MARGIN, 52);
  // doc.text(`PAN: ${PCM_INFO.pan}`, PAGE_W - MARGIN, 52, { align: "right" });

  hr(doc, 54, PRIMARY);
};

// ── page footer ───────────────────────────────────────────────────────────────
const drawFooter = (doc, pageNum, totalPages) => {
  const y = PAGE_H - 10;
  hr(doc, y - 3, [209, 213, 219]);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...MID);
  doc.text(`${PCM_INFO.name} — Terms & Conditions`, MARGIN, y);
  doc.text(`Page ${pageNum} of ${totalPages}`, PAGE_W - MARGIN, y, {
    align: "right",
  });
};

// ── section renderer ──────────────────────────────────────────────────────────
const renderSection = (doc, section, cursorY, addPage) => {
  const BOTTOM_LIMIT = PAGE_H - 18;

  // Section heading
  if (cursorY + 8 > BOTTOM_LIMIT) cursorY = addPage();

  fillRect(doc, MARGIN - 2, cursorY - 4, CONTENT_W + 4, 8, 2, LIGHT);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...DARK);
  doc.text(section.heading || "", MARGIN, cursorY + 0.5);

  cursorY += 7;

  // Intro
  if (section.intro) {
    const introLines = wrap(doc, section.intro, CONTENT_W);

    if (cursorY + introLines.length * 4.5 + 3 > BOTTOM_LIMIT) {
      cursorY = addPage();
    }

    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(...MID);

    doc.text(introLines, MARGIN, cursorY);

    cursorY += introLines.length * 4.5 + 3;
  }

  // Groups
  for (const group of section.groups || []) {
    // Sub heading
    if (group.subHeading) {
      if (cursorY + 6 > BOTTOM_LIMIT) {
        cursorY = addPage();
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...PRIMARY);

      doc.text(group.subHeading, MARGIN, cursorY);

      cursorY += 5;
    }

    // Items
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.2);
    doc.setTextColor(55, 65, 81);

    for (const item of group.items || []) {
      const lines = wrap(doc, item, CONTENT_W - 8);

      const blockH = lines.length * 4.5 + 2;

      if (cursorY + blockH > BOTTOM_LIMIT) {
        cursorY = addPage();
      }

      doc.setFillColor(...PRIMARY);
      doc.circle(MARGIN + 1.5, cursorY - 1, 0.9, "F");

      doc.text(lines, MARGIN + 5, cursorY);

      cursorY += blockH;
    }

    // Note box
    if (group.note) {
      const noteLines = wrap(doc, group.note, CONTENT_W - 10);

      const noteHeight = noteLines.length * 4.5 + 6;

      if (cursorY + noteHeight > BOTTOM_LIMIT) {
        cursorY = addPage();
      }

      fillRect(
        doc,
        MARGIN,
        cursorY - 2,
        CONTENT_W,
        noteHeight,
        2,
        [255, 251, 235],
      );

      doc.setFont("helvetica", "italic");
      doc.setFontSize(7.5);
      doc.setTextColor(146, 64, 14);

      doc.text(noteLines, MARGIN + 3, cursorY + 2);

      cursorY += noteHeight + 2;
    }

    cursorY += 2;
  }

  cursorY += 3;

  return cursorY;
};

export const generateTermsPDF = ({
  logoDataUrl = null,
  termsSections = [],
  declarationContents = [],
  acceptedBy = "",
}) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  let pages = 1;
  const pageYStarts = []; // track y where each page's content starts

  // ── add-page helper ──────────────────────────────────────────────────────
  const addPage = () => {
    doc.addPage();
    pages += 1;
    drawHeader(doc, logoDataUrl);
    return 62; // content starts after header
  };

  // ── PAGE 1 ────────────────────────────────────────────────────────────────
  drawHeader(doc, logoDataUrl);
  let y = 62;

  // Document title block
  fillRect(doc, MARGIN, y, CONTENT_W, 14, 3, [254, 242, 242]);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...PRIMARY);
  doc.text("TERMS & CONDITIONS", PAGE_W / 2, y + 6, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...MID);
  doc.text(
    `Effective Date: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}`,
    PAGE_W / 2,
    y + 11,
    { align: "center" },
  );
  y += 18;

  // Intro blurb
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.8);
  doc.setTextColor(...MID);
  const introLines = wrap(
    doc,
    "Please read these Terms & Conditions carefully before using the Quick Quotation & Trade (QQT) platform. By accessing or using our services, you agree to be bound by these terms.",
    CONTENT_W,
  );
  doc.text(introLines, MARGIN, y);
  y += introLines.length * 4.5 + 4;
  hr(doc, y, [209, 213, 219]);
  y += 5;

  // ── Render all T&C sections ───────────────────────────────────────────────
  for (const section of termsSections) {
    y = renderSection(doc, section, y, addPage);
  }

  // ── Declaration / Key Points section ─────────────────────────────────────
  if (declarationContents.length > 0) {
    if (y + 30 > PAGE_H - 18) y = addPage();

    hr(doc, y, PRIMARY);
    y += 6;

    fillRect(doc, MARGIN - 2, y - 4, CONTENT_W + 4, 9, 2, [254, 242, 242]);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...PRIMARY);
    doc.text("KEY DECLARATIONS", MARGIN, y + 1.5);
    y += 9;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.2);

    declarationContents.forEach((item, idx) => {
      const lines = wrap(doc, item.content, CONTENT_W - 12);
      const blockH = lines.length * 4.5 + 4;

      if (y + blockH > PAGE_H - 18) y = addPage();

      // Number badge
      fillRect(doc, MARGIN, y - 4, 7, 7, 1.5, PRIMARY);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...WHITE);
      doc.text(String(idx + 1), MARGIN + 3.5, y + 0.3, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.2);
      doc.setTextColor(55, 65, 81);
      doc.text(lines, MARGIN + 10, y);
      y += blockH;
    });
  }

  // ── Acceptance block ──────────────────────────────────────────────────────
  const blockH = 28;
  if (y + blockH > PAGE_H - 18) y = addPage();

  y += 4;
  fillRect(doc, MARGIN, y, CONTENT_W, blockH, 3, [240, 253, 244]); // green-50
  doc.setDrawColor(134, 239, 172); // green-300
  doc.setLineWidth(0.4);
  doc.roundedRect(MARGIN, y, CONTENT_W, blockH, 3, 3, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(22, 101, 52); // green-800
  doc.text("✓  Accepted & Agreed", MARGIN + 5, y + 7);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.8);
  doc.setTextColor(21, 128, 61); // green-700
  if (acceptedBy) {
    doc.text(`Accepted by: ${acceptedBy}`, MARGIN + 5, y + 13);
  }
  doc.text(
    `Date & Time: ${new Date().toLocaleString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })}`,
    MARGIN + 5,
    y + 19,
  );
  doc.setFontSize(7);
  doc.setTextColor(...MID);
  doc.text(
    "This document is a digitally generated record of acceptance.",
    MARGIN + 5,
    y + 25,
  );

  // ── Draw footers on all pages ─────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, i, totalPages);
  }

  const fileName = `PCM_Exports_Terms_and_Conditions_${
    new Date().toISOString().split("T")[0]
  }.pdf`;

  // Download for user
  doc.save(fileName);

  // Return blob so it can be uploaded to Cloudinary
  return {
    blob: doc.output("blob"),
    fileName,
  };
};
